import "./portal.scss";

import { defer, delay, each, last } from "lodash";
import PropTypes from "prop-types";
import React from "react";

import { stripElementIds } from "../../utils/domUtils";
import uniqueId from "../../utils/uniqueId";
// import { PropTypeShapes } from "../../types";
import createDomNode from "./createDomNode";
import { PortalContext } from "./PortalContext";
import TransitionClasses from "./TransitionClasses";
// import { TransitionGroup } from "react-transition-group";

export const PORTAL_CLASS_NAME = "slidePortal";

const applyTransitionClass = (node, className) => {
  if (node) {
    node.classList.add(className);
    each(TransitionClasses, n => {
      if (n !== className) {
        node.classList.remove(n);
      }
    });
  }
};

const duplicateNodeWithId = id => {
  const origNode = document.getElementById(id);
  // Copy the node we are about to remove
  let nodeCopy = null;

  if (origNode) {
    // Take a clone of the node
    nodeCopy = origNode.cloneNode(true);
    // Remove its IDs to avoid duplicate IDs in the DOM
    stripElementIds(nodeCopy);
  }
  return nodeCopy;
};

class Portal extends React.PureComponent {
  constructor(props, context) {
    super(props, context);

    this._pageHistory = [];
    this._currentPage = null;

    this.onChildDidMount = this.onChildDidMount.bind(this);
    this.onChildWillUnmount = this.onChildWillUnmount.bind(this);
    this.removeExitingNode = this.removeExitingNode.bind(this);
    this.slideToNewChild = this.slideToNewChild.bind(this);
    this.slideToPreviousChild = this.slideToPreviousChild.bind(this);

    this._nodeId = "x" + uniqueId();
    this._node = createDomNode(this.props.targetDomId, this._nodeId, PORTAL_CLASS_NAME);

    this._exitingNode = null;

    this._portalContext = {
      portalNode: this._node,
      portalNodeId: this._nodeId,
      onDidMount: this.onChildDidMount,
      onWillUnmount: this.onChildWillUnmount
    };
  }

  componentWillUnmount() {
    if (this._node) {
      this._node.remove();
    }
    this._node = null;
    this._created = false;
  }

  onChildDidMount(childId) {
    // Add to our list of children
    this._pageHistory.push(childId);

    // If this is the first child, then we do not want to animate it in
    // Instead, we just want it to show up
    if (this._pageHistory.length == 1) {
      this.slideToNewChild();
      return;
    }
    // Otherwise, we will want to animate this node in, so we have to set it up
    // offscreen first
    const node = document.getElementById(childId);
    applyTransitionClass(node, TransitionClasses.pending);

    // After a beat, tell it to start transitioning
    defer(this.slideToNewChild);
  }

  onChildWillUnmount(childId) {
    // Remove from our list of children
    this._pageHistory.pop();

    // If we already have another exiting node, get rid of it
    if (this._exitingNode) {
      this.removeExitingNode();
    }

    // If there are no pages left, no point sliding this one in or out
    // Just let it vanish
    if (this._pageHistory.length == 0) {
      this._currentPage = null;
      return;
    }

    // Otherwise, we want to show an animation of this node leaving
    // So we don't interfere with the React lifecycle, we will make
    // a clone of the node that we can move around without rising a
    // re-render

    // Copy the node we are about to remove
    this._exitingNode = duplicateNodeWithId(childId);
    // And add it to the DOM
    if (this._node) {
      this._node.appendChild(this._exitingNode);
    }

    // After a beat, tell it to start transitioning
    defer(this.slideToPreviousChild);

    // Once it has had a chance to animate out, remove it entirely
    delay(this.removeExitingNode, 600);
  }

  slideToNewChild() {
    // The previous current child is still stored here, so tell it to
    // move
    if (this._currentPage) {
      const prevNode = document.getElementById(this._currentPage);
      applyTransitionClass(prevNode, TransitionClasses.history);
    }
    // Now update our currentChild reference
    this._currentPage = last(this._pageHistory);

    if (this._currentPage) {
      // And tell that node to move into the active position
      const currNode = document.getElementById(this._currentPage);
      applyTransitionClass(currNode, TransitionClasses.active);
    }
  }

  slideToPreviousChild() {
    if (this._exitingNode) {
      // Tell our cloned node to start exiting
      applyTransitionClass(this._exitingNode, TransitionClasses.exiting);
    }
    // Update to our new last child
    this._currentPage = last(this._pageHistory);
    if (this._currentPage) {
      // And tell it to become active again
      const currNode = document.getElementById(this._currentPage);
      applyTransitionClass(currNode, TransitionClasses.active);
    }
  }

  removeExitingNode() {
    if (this._exitingNode) {
      this._exitingNode.remove();
      this.exitingNode = null;
    }
  }

  render() {
    return <PortalContext.Provider value={this._portalContext}>{this.props.children}</PortalContext.Provider>;
  }
}

Portal.propTypes = {
  children: PropTypes.node.isRequired,
  targetDomId: PropTypes.string
};

export default Portal;
